<script setup lang="ts">
/**
 * Paying for a lesson inside its last 48 hours, at the moment it is asked for
 * or agreed: the family chooses a card, and the price is held on it, with
 * their bank's approval where it asks. The request goes ahead only once it is.
 */
import { nextTick, ref, watch } from 'vue'
import { Button } from '@/Components/ui/button'
import CardPicker from '@/Components/Payment/CardPicker.vue'
import { pendingLessonPayment } from '@/lib/lessonPayment'
import { loadStripe } from '@/lib/stripe'

const props = defineProps<{
  stripeKey: string
}>()

const error = ref('')
const isHolding = ref(false)
const panel = ref<HTMLElement | null>(null)
const picker = ref<InstanceType<typeof CardPicker> | null>(null)

// Focus goes to the dialog while it is open, and back to where it was after.
let returnFocusTo: HTMLElement | null = null

watch(pendingLessonPayment, async (request, previous) => {
  if (request && !previous) {
    returnFocusTo = document.activeElement as HTMLElement | null
    await nextTick()
    panel.value?.focus()
  } else if (!request && previous) {
    returnFocusTo?.focus()
  }
})

function back() {
  error.value = ''
  pendingLessonPayment.value?.settle(null)
}

async function hold() {
  const request = pendingLessonPayment.value

  isHolding.value = true
  error.value = ''

  try {
    const { paymentMethodId, remember } = await picker.value!.choose()

    const { data } = await window.axios.post(`/lesson-changes/arrangements/${request.subscriptionId}/payment-hold`, {
      payment_method_id: paymentMethodId,
    })

    if (data.status === 'requires_action') {
      const stripe = await loadStripe(props.stripeKey)
      const { error: bankError, paymentIntent } = await stripe.handleNextAction({ clientSecret: data.client_secret })

      if (bankError || paymentIntent?.status !== 'requires_capture') {
        error.value = bankError?.message ?? 'Your bank did not confirm the payment.'
        return
      }
    } else if (data.status !== 'requires_capture') {
      error.value = data.message ?? 'Your bank did not accept the payment.'
      return
    }

    request.settle({ paymentIntentId: data.payment_intent_id, paymentMethodId, remember })
  } catch (failure: any) {
    error.value = failure?.response?.data?.message ?? failure?.message ?? 'The payment did not go through. Please try again.'
  } finally {
    isHolding.value = false
  }
}
</script>

<template>
  <div
    v-if="pendingLessonPayment"
    class="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-4"
    @click.self="!isHolding && back()"
  >
    <div
      ref="panel"
      role="dialog"
      tabindex="-1"
      aria-modal="true"
      aria-labelledby="lesson-payment-title"
      class="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl"
      @keydown.esc="!isHolding && back()"
    >
      <h3 id="lesson-payment-title" class="text-lg font-semibold text-gray-900">
        Pay £{{ pendingLessonPayment.amount.toFixed(2) }} for this lesson
      </h3>
      <p class="mt-1 text-sm text-gray-500">{{ pendingLessonPayment.message }}</p>

      <div class="mt-4">
        <CardPicker ref="picker" :stripe-key="stripeKey" />
      </div>

      <p v-if="error" role="alert" class="mt-3 text-sm text-red-700">{{ error }}</p>

      <div class="mt-5 flex gap-2">
        <Button :disabled="isHolding || !picker?.isReady" @click="hold">
          {{ isHolding ? 'Confirming…' : 'Confirm the payment' }}
        </Button>
        <Button variant="ghost" :disabled="isHolding" @click="back">Back</Button>
      </div>
    </div>
  </div>
</template>
